import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Layers, Wallet } from 'lucide-react';
import api from '../utils/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (user) {
      api.get('/balance').then(({ data }) => setBalance(data.balance)).catch(() => {});
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:shadow-green-500/60 transition-shadow">
              <Layers className="w-5 h-5 text-gray-950" />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">
              Plarix<span className="text-green-400">MC</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/pricing" className="text-gray-400 hover:text-white text-sm transition-colors">Pricing</Link>
            <Link to="/features" className="text-gray-400 hover:text-white text-sm transition-colors">Features</Link>
            {user && <Link to="/tickets" className="text-gray-400 hover:text-white text-sm transition-colors">Support</Link>}
          </div>

          {/* Auth buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {/* Balance badge */}
                {balance !== null && (
                  <Link
                    to="/balance"
                    className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
                  >
                    <Wallet className="w-3.5 h-3.5 text-green-400" />
                    <span className="font-semibold">€{parseFloat(balance).toFixed(2)}</span>
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="text-sm bg-green-500 hover:bg-green-400 text-gray-950 font-semibold px-3 py-1.5 rounded-lg transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/account"
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Account
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Admin
                  </Link>
                )}
                {['admin', 'support_agent'].includes(user.role) && (
                  <Link
                    to="/staff"
                    className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Staff Panel
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="text-sm bg-green-500 hover:bg-green-400 text-gray-950 font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-gray-400 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-gray-900 border-t border-gray-800 px-4 py-4 space-y-3">
          <Link to="/pricing" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Pricing</Link>
          <Link to="/features" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Features</Link>
          {user ? (
            <>
              <Link to="/dashboard" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <Link to="/account" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Account Settings</Link>
              {balance !== null && (
                <Link to="/balance" className="flex items-center gap-2 text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>
                  <Wallet className="w-4 h-4 text-green-400" />
                  Balance: <span className="font-semibold text-green-400">€{parseFloat(balance).toFixed(2)}</span>
                </Link>
              )}
              <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block text-gray-400 py-2">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Login</Link>
              <Link to="/register" className="block bg-green-500 text-gray-950 font-semibold px-4 py-2 rounded-lg text-center" onClick={() => setMobileOpen(false)}>Get Started Free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
